import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerApi } from '../api/customers';
import { isApiError, refusalMessage } from '../api/errors';
import { publicSettings } from '../api/publicSettings';
import { riderApi } from '../api/riders';
import DriverPasswordField from '../components/driver/DriverPasswordField';
import { getCustomerSession, isCustomerMarkedSignedIn } from '../lib/customerSession';
import { isDriverSignedIn } from '../lib/driverSession';
import './driverLoginPremium.css';

/**
 * The public page Google Play asks for (InGo ADR 0038): deleting an InGo
 * account without the app. It signs in with the realm's own sign-in, then
 * makes the same request as "Delete my account" in the app, and keeps no
 * session in this browser: on success the server has ended it, on a refusal
 * this page signs out. Someone already signed in here is sent to their
 * profile instead, so their session is never overwritten.
 *
 * An account made with Google has no password to type here: those people use
 * the app, or email the office from the account's own address.
 */
export default function PublicDeleteAccountPage({ customers = customerApi, riders = riderApi, settings = publicSettings }) {
  const [realm, setRealm] = useState('customer');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const [office, setOffice] = useState(null);

  useEffect(() => {
    let gone = false;
    settings.contacts().then(
      (contacts) => !gone && setOffice(contacts?.email ?? null),
      () => {},
    );

    return () => {
      gone = true;
    };
  }, [settings]);

  const signedInHere = isDriverSignedIn() || (isCustomerMarkedSignedIn() && getCustomerSession() !== null);

  const submit = async (e) => {
    e.preventDefault();
    setProblem('');
    if (!email.trim() || !password) {
      setProblem('Enter the email address and password you sign in with.');
      return;
    }
    const realmApi = realm === 'driver' ? riders : customers;
    setBusy(true);
    let session = false;
    try {
      await realmApi.signIn(email.trim().toLowerCase(), password);
      session = true;
      const { message } = await realmApi.requestDeletion({ password });
      // The server ended every session, and the token is forgotten.
      session = false;
      setDone(message);
    } catch (error) {
      if (session) await realmApi.signOut();
      setProblem(isApiError(error) && error.code === 'ACCOUNT_TIED' ? error.message : refusalMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dp-login-page">
      <main className="dp-login-body dp-login-body--alone">
        <div className="dp-login-card">
          <h1 className="dp-login-title">Delete your InGo account</h1>
          <p className="dp-login-subtitle">
            For anyone with an InGo account, whether you send parcels or deliver them. In the app it is in your profile: Delete my account.
          </p>
          <p className="dp-login-subtitle">
            Your account is deleted 30 days after you ask. You are signed out on every phone at once, and we email you a link: choosing a
            new password from it keeps your account. After 30 days your name and contact details are erased. Your past deliveries stay in
            InGo&apos;s records, without them.
          </p>

          {done ? (
            <p className="dp-login-flash" role="status">
              {done}
            </p>
          ) : signedInHere ? (
            <p className="dp-login-flash" role="status">
              You are signed in to InGo in this browser. Open your profile and choose Delete my account:{' '}
              <Link to={isDriverSignedIn() ? '/driver/profile' : '/profile'}>your profile</Link>.
            </p>
          ) : (
            <form className="dp-login-form" onSubmit={submit} noValidate>
              <fieldset className="dp-login-field" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="dp-login-label">Your account</legend>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0.35rem 0' }}>
                  <input type="radio" name="realm" value="customer" checked={realm === 'customer'} onChange={() => setRealm('customer')} />I
                  send parcels with InGo
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0.35rem 0' }}>
                  <input type="radio" name="realm" value="driver" checked={realm === 'driver'} onChange={() => setRealm('driver')} />I deliver
                  for InGo
                </label>
              </fieldset>

              <div className="dp-login-field">
                <label className="dp-login-label" htmlFor="pda-email">
                  Email address
                </label>
                <div className="dp-login-input-wrap">
                  <input
                    className="dp-login-input"
                    id="pda-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={busy}
                  />
                </div>
              </div>
              <DriverPasswordField
                id="pda-password"
                label="Password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                disabled={busy}
              />

              {problem ? (
                <p className="dp-login-error" role="alert">
                  {problem}
                </p>
              ) : null}

              <button type="submit" className="dp-login-btn dp-login-btn--danger" disabled={busy}>
                {busy ? 'Asking…' : 'Delete my account'}
              </button>
            </form>
          )}

          <p className="dp-login-customer-line">
            Signed up with Google? Use Delete my account in the app, or email{' '}
            {office ? <a href={`mailto:${office}?subject=Delete%20my%20InGo%20account`}>{office}</a> : 'the InGo office'} from your
            account&apos;s own address.
          </p>
        </div>
      </main>
    </div>
  );
}
